import React, { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import TaskPrompt from "@/components/tasks/TaskPrompt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCheckCircle,
  faEdit,
  faInfoCircle,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";

import { TaskFormData, TaskProps } from "@/components/tasks/types";
import { apiClient } from "@/api";
import toast from "@/utils/toast";
import { useNavigate } from "react-router";

import TaskPromptComments from "@/components/tasks/discuss/TaskPromptComments";
import RichTextDisplay from "@/components/widgets/richtext/RichTextDisplay";
import { ReviewState, TaskPrompt as TaskPromptType, TaskResponse } from "@/api/types";
import { sanitizeName } from "@/utils/strings";
import AppButton from "@/components/structure/AppButton";
import ChatBox from "./review/ChatBox";
import AlertBox from "../structure/AlertBox";
import PeekoutDrawer from "../structure/PeekoutDrawer";
import ScrollToBottomButton from "../structure/ScrollToBottomButton";

// if true, shows the content of the LLM chat inline in the page
// if false, shows the chat in a PeekoutDrawer
const inlineChatInPage = false;

export default function Task({
  id,
  task,
  project,
  readOnly,
  isReviewing = false,
  isDiscussing = false,
  showTaskSummary = true,
}: TaskProps) {
  const navigate = useNavigate();

  const [awaitingReview, setAwaitingReview] = useState(false);

  // visibility of the chatbox drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { prompts, title, description, instructions } = task || {};

  // Initialize form with default values from prompts
  const defaultValues = prompts?.reduce((acc, prompt, index) => {
    const fieldName = sanitizeName(`prompt_${prompt.name}`);
    acc[fieldName] = prompt.type === "checkbox" ? false : "";
    return acc;
  }, {} as TaskFormData);

  const methods = useForm<TaskFormData>({
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = methods;

  // Reset form when prompts change
  useEffect(() => {
    const newDefaultValues = prompts?.reduce((acc, prompt, index) => {
      const fieldName = sanitizeName(`prompt_${prompt.name}`);
      acc[fieldName] = prompt.type === "checkbox" ? false : "";
      return acc;
    }, {} as TaskFormData);
    reset(newDefaultValues);
  }, [prompts, reset]);

  const unmetTaskDependency =
    task?.userDependencyStatus && !task?.userDependencyStatus?.canRespond;

  const submitDisabled = useMemo(() => {
    return !isValid || awaitingReview || unmetTaskDependency;
  }, [isValid, awaitingReview, unmetTaskDependency]);

  if (!prompts || prompts.length === 0) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <p className="text-gray-500 text-lg">No prompts to display</p>
        <p className="text-gray-400 text-sm mt-2">
          Add some prompts to see them here
        </p>
      </div>
    );
  }

  const onSubmit = (data: TaskFormData) => {
    if (!id) {
      console.error("Task ID is required to submit response");
      return;
    }

    console.log("Form data: ", data);

    apiClient
      .post<TaskResponse>(`/task-responses/`, {
        task: id,
        promptResponses: prompts.map((prompt, index) => ({
          taskPrompt: prompt.id,
          responseText: String(data[sanitizeName(`prompt_${prompt.name}`)]),
        })),
      })
      .then((response?: TaskResponse) => {
        console.log("Task submitted successfully:", response);
        toast.info("Task submitted successfully");

        // if response.reviewStatus is pending, poll every 1s for up to 10 seconds
        if (response?.reviewStatus === ReviewState.Pending) {
          setAwaitingReview(true);
          let attempts = 0;

          const interval = setInterval(() => {
            attempts += 1;

            apiClient
              .get<TaskResponse>(`/task-responses/${response.id}/`)
              .then((res?: TaskResponse) => {
                if (!res) return;

                if (res.reviewStatus === ReviewState.Approved || res.reviewStatus === ReviewState.NeedsReview) {
                  clearInterval(interval);
                  setAwaitingReview(false);

                  if (res.reviewStatus === ReviewState.NeedsReview) {
                    toast.info("Your task response needs further input");
                    // navigate to the review page
                    navigate(`/tasks/${id}/review`);
                  }
                  else {
                    toast.info("Your task response has been approved");

                    if (task?.isDiscussable) {
                      // if the task is discussable, go to the discuss page
                      navigate(`/tasks/${id}/discuss`);
                    }
                    else {
                      // if it's not discussable, go back to the project page, if specified
                      if (project?.id) {
                        navigate(`/projects/${project.id}`);
                      }
                      else {
                        // just go home since we have no project context
                        navigate(`/`);
                      }
                    }
                  }
                } else if (attempts >= 10) {
                  clearInterval(interval);
                  toast.info(
                    "Your task response is still pending review. Please check back later."
                  );

                  // navigate back to the project page, if specified
                  if (project?.id) {
                    navigate(`/projects/${project.id}`);
                  }
                }
              })
              .catch((error) => {
                clearInterval(interval);
                setAwaitingReview(false);
                console.error("Error checking review status:", error);
                toast.apiError(
                  "Failed to check review status",
                  error
                );
              })
          }, 1000);
        }
        else {
          // navigate back to the project page, if specified
          if (project?.id) {
            navigate(`/projects/${project.id}`);
          }
        }
      })
      .catch((error) => {
        console.error("Error submitting task:", error);
        toast.apiError("Failed to submit task response", error);
      });
  };

  const taskBody = (
    <>
      {/* Task Header */}
      <div className="pb-1">
        {title && (
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
        )}
        {description && <p className="text-gray-600">{description}</p>}
        {instructions && (
          <RichTextDisplay
            content={instructions}
            className="prose prose-sm max-w-none"
          />
        )}
      </div>

      {/* Unmet Dependent Tasks */}
      {unmetTaskDependency && (
        <AlertBox type="warning" headerText="Task Dependencies">
          You must first complete the following task(s) before you can respond
          to this one:
          <div className="mt-2 space-x-2">
            {task.userDependencyStatus!.unmetDependencies.map((dep) => (
              <AppButton
                type="button"
                key={dep.taskId}
                variant="warning"
                forwardArrow={true}
                href={`/tasks/${dep.taskId}`}
              >
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                {dep.title || `Task ID: ${dep.taskId}`}
              </AppButton>
            ))}
          </div>
        </AlertBox>
      )}

      {/* Task Summary */}
      {showTaskSummary && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-blue-800 font-medium">
            <FontAwesomeIcon
              className="text-blue-500 text-lg pr-2"
              icon={faInfoCircle}
            />{" "}
            Task Summary
          </div>
          <p className="text-gray-600 text-sm mt-2 mb-0">
            This task contains {prompts.length} prompt
            {prompts.length !== 1 ? "s" : ""} to complete.
          </p>
        </div>
      )}

      {/* Task Prompts */}
      <div
        className={`space-y-4 ${
          unmetTaskDependency ? "opacity-50 pointer-events-none" : ""
        }`}
      >
        {prompts.map((prompt, index) => {
          // if (prompt.type === "convo-break" && (isDiscussing || isReviewing)) {
          //   // Skip convo-break prompts unless editing
          //   return null;
          // }

          return (
            <div key={index} className="relative">
              {/* Prompt number indicator */}
              <div className="flex items-start space-x-4 mb-4">
                <div
                  className={`flex-shrink-0 w-8 h-8 ${
                    prompt.type === "convo-break"
                      ? "text-gray-800 bg-gray-100"
                      : "text-blue-800 bg-blue-100"
                  }  rounded-full flex items-center justify-center text-sm font-medium mt-2`}
                >
                  {index + 1}
                </div>

                <div className="flex-1">
                  <TaskPrompt
                    prompt={prompt}
                    showValue={!isDiscussing}
                    readOnly={readOnly}
                    fieldName={sanitizeName(`prompt_${prompt.name}`)}
                  />
                </div>
              </div>

              {isDiscussing && prompt.id && id && (
                <div className="mb-8 ml-12">
                  <TaskPromptComments
                    task={task}
                    prompt={prompt as TaskPromptType}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit Task Button */}
      {!readOnly && (
        <div className="mt-6 flex justify-center">
          <button
            type="submit"
            disabled={submitDisabled}
            className={`w-50 py-2 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
              !submitDisabled
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {awaitingReview && (
              <FontAwesomeIcon icon="spinner" spin className="mr-2" />
            )}
            Submit Response
          </button>
          {/* {Object.keys(errors).length > 0 && (
              <div className="ml-4 flex items-center text-red-600">
                <span className="text-sm">Please fix the errors above</span>
              </div>
            )} */}
        </div>
      )}

      { /* Chat with LLM about the response */ }
      {isReviewing && task?.lastResponse && (
        inlineChatInPage ? (
          <>
            <div className="mt-10">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Discuss this task response</h3>
              <div className="border border-gray-200 rounded-lg p-4">
                <ChatBox taskResponse={task.lastResponse} />
              </div>
            </div>
            <ScrollToBottomButton />
          </>
        ) : (
          <PeekoutDrawer title={<><FontAwesomeIcon icon="robot" className="mr-1" /> LLM Discussion</> } shortName="Discussion" open={drawerOpen} setOpen={setDrawerOpen} >
            <ChatBox taskResponse={task.lastResponse} />
          </PeekoutDrawer>
        )
      )}

      {/* Complete Review Button */}
      {isReviewing && task?.lastResponse && task.lastResponse.reviewStatus === ReviewState.NeedsReview && (
        <div className="mt-6 flex justify-center">
          <AppButton
            type="button"
            variant="primary"
            onClick={() => {
              apiClient.put<TaskResponse>(`/task-responses/${task.lastResponse?.id}/`, {
                task: id,
                reviewStatus: ReviewState.Approved,
              })
              .then((response?: TaskResponse) => {
                toast.info("Review completed successfully");
                // navigate to the discuss page
                navigate(`/projects/${project?.id}`);
              })
              .catch((error) => {
                console.error("Error completing review:", error);
                toast.apiError("Failed to complete review", error);
              });
            }}
          >
            <FontAwesomeIcon icon={faCheckCircle} className="text-xl mr-2" />
            Complete Review
          </AppButton>
        </div>
      )}
    </>
  );

  return (
    <FormProvider {...methods}>
      {isDiscussing || isReviewing ? (
        taskBody
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {taskBody}
        </form>
      )}
    </FormProvider>
  );
}
